import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useSessionStore } from "@/stores";
import type ITradeItem from "@/models/responses/ITradeItem";

export const useTradeItemsStore = defineStore("tradeitems", {
  state: () => ({
    error: "" as any,
    data: [] as ITradeItem[],
    loadedTenantId: 0,
  }),
  actions: {
    async fetchAll(force = false) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedTenantId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loadedTenantId === selectedTenantId.value) return;

      this.error = "";
      await api
        .get<ITradeItem[]>(`v1.0/tradeitems?tenantId=${selectedTenantId.value}`)
        .then(response => {
          this.data = response.data;
          this.loadedTenantId = selectedTenantId.value;
         })
        .catch(error => this.error = error);
    },
    async sync(itemId: number, tradeItems: ITradeItem[]) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .post<ITradeItem[]>(`v1.0/tradeitems/sync`, {
          itemId: itemId,
          tradeItems: tradeItems
        })
        .then((response) => {
          // remove barcodes that are equal to the requested itemId, because the response contains all barcodes for the items
          this.data = this.data.filter((x) => x.itemId !== itemId);
          response.data.forEach((tradeItem) => {
            const index = this.data.findIndex((x) => x.id === tradeItem.id);
            if (index === -1) this.data.push(tradeItem);
            else this.data[index] = tradeItem;
          });
        })
        .catch((error) => {
          this.error = error;
        });
    },
  },
  getters: {
    get:
      (state) => (id: number): ITradeItem => {
        return <ITradeItem>(
          (state.data as ITradeItem[])
            .find((tradeItem) => tradeItem.id === id)
        );
      },
    getByCode:
      (state) => (code: string): ITradeItem => {
        return <ITradeItem>(
          (state.data as ITradeItem[])
            .find((tradeItem) => tradeItem.code.padStart(14, '0') === code.padStart(14, '0'))
        );
      },
    getByItemId:
      (state) => (itemId: number): ITradeItem[] => {
        return <ITradeItem[]>(
          (state.data as ITradeItem[])
            .filter((tradeItem) => tradeItem.itemId === itemId)
            .sort((a, b) => a.factor - b.factor || a.unitCode.localeCompare(b.unitCode))
        );
      },
    getByItemIdAndUnitCode:
      (state) => (itemId: number, unitCode: string): ITradeItem => {
        return <ITradeItem>(
          (state.data as ITradeItem[])
            .find((tradeItem) => tradeItem.itemId === itemId && tradeItem.unitCode === unitCode)
        );
      },
    getBaseByItemId:
      (state) => (itemId: number): ITradeItem => {
        return <ITradeItem>(
          (state.data as ITradeItem[])
            .sort((a, b) => a.unitCode.localeCompare(b.unitCode))
            .find((item: ITradeItem) => item.itemId === itemId && item.factor === 1)
        );
      }
  },
});
